'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { paydirtConfig } from '@/config/paydirt';
import { useAuth } from '@/platform/auth/useAuth';
import { displayHandle } from '@/platform/users/displayHandle';
import { ThemeSettingsModal } from '@/domains/paydirt/theme/ThemeSettings';
import { isNavActive, SIDEBAR_NAV_AFTER_MARKETPLACE, SIDEBAR_NAV_MAIN } from '@/domains/paydirt/nav';
import { useIsPriceGuideRoute } from '@/domains/paydirt/usePriceGuideRoute';
import { isOpsItsmBuild } from '@/platform/servicedesk/isOpsItsmBuild';
import { PaydirtMarketplaceSidebarAccordion } from './PaydirtMarketplaceSidebarAccordion';

export function PaydirtSidebar() {
  if (isOpsItsmBuild()) {
    return null;
  }
  const pathname = usePathname() ?? '/';
  const hideClaimCta = useIsPriceGuideRoute();
  const { user, profile } = useAuth();
  const [themeOpen, setThemeOpen] = useState(false);
  const displayName = profile?.displayName ?? user?.displayName ?? 'Member';

  return (
    <aside className="paydirt-sidebar">
      {user && (
        <Link href={`/users/${user.uid}/`} className="paydirt-sidebar__profile">
          <div className="profile-photo">
            {user.photoURL ? (
              <img src={user.photoURL} alt="" />
            ) : (
              (displayName[0] ?? user.email?.[0] ?? '?').toUpperCase()
            )}
          </div>
          <div>
            <h4 style={{ margin: 0, fontSize: '0.95rem' }}>{displayName}</h4>
            <p className="text-muted" style={{ margin: 0, fontSize: '0.8rem' }}>
              @{displayHandle(profile?.handle, user.uid)}
            </p>
          </div>
        </Link>
      )}
      <nav className="paydirt-sidebar__menu" aria-label="Sidebar">
        {SIDEBAR_NAV_MAIN.map((item) => (
          <Link
            key={item.href}
            href={item.href}
            className={`paydirt-sidebar__item${isNavActive(pathname, item.href) ? ' paydirt-sidebar__item--active' : ''}`}
            title={'hint' in item ? item.hint : undefined}
          >
            <i className={`uil ${item.icon}`} aria-hidden />
            <span>{item.label}</span>
          </Link>
        ))}
        <PaydirtMarketplaceSidebarAccordion pathname={pathname} />
        {SIDEBAR_NAV_AFTER_MARKETPLACE.map((item) => (
          <Link
            key={item.href}
            href={item.href}
            className={`paydirt-sidebar__item${isNavActive(pathname, item.href) ? ' paydirt-sidebar__item--active' : ''}`}
            title={'hint' in item ? item.hint : undefined}
          >
            <i className={`uil ${item.icon}`} aria-hidden />
            <span>{item.label}</span>
          </Link>
        ))}
        <button
          type="button"
          className="paydirt-sidebar__item"
          onClick={() => setThemeOpen(true)}
        >
          <i className="uil uil-palette" aria-hidden />
          <span>Theme</span>
        </button>
      </nav>
      <ThemeSettingsModal open={themeOpen} onClose={() => setThemeOpen(false)} />
      {!hideClaimCta && (
        <Link href="/claims/new/" className="btn btn-primary paydirt-sidebar__cta">
          {paydirtConfig.claimCtaLabel}
        </Link>
      )}
    </aside>
  );
}
